import React, { useContext, useMemo, type ReactNode } from 'react';
import { BulkOpsAnalyticsProvider } from '@atlassian/jira-bulk-operations-analytics/src/controllers/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { createUseEntryPointContext } from '@atlassian/jira-entry-point/src/controllers/utils/create-use-entry-point-context/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useProjectKeyFromPath } from '@atlassian/jira-bulk-operations-common/src/utils/use-project-key-from-path/useProjectKeyFromPath.tsx';
import { asyncBulkOpsCoreEntryPoint } from '../entrypoint.tsx';

export const useBulkopsCoreEntryPoint = () => {
	const cloudId = useCloudId();
	const projectKey = useProjectKeyFromPath();

	const entryPointParams = useMemo(() => ({ cloudId, projectKey }), [cloudId, projectKey]);

	const entryPointResult = useEntryPoint(asyncBulkOpsCoreEntryPoint, entryPointParams);
	return entryPointResult;
};

const BulkOpsCoreEntryPointContext =
	createUseEntryPointContext<typeof asyncBulkOpsCoreEntryPoint>();

// Will be removed when cleaning up `bulk_delete_experiment`
// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
const emptyObject = {} as any;

const BulkOpsCoreEntryPointProvider = ({ children }: { children: ReactNode }) => {
	const entryPointResult = expVal('bulk_delete_experiment', 'isEnabled', false)
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useBulkopsCoreEntryPoint()
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			useEntryPoint(asyncBulkOpsCoreEntryPoint, emptyObject);

	return (
		<BulkOpsCoreEntryPointContext.Provider value={entryPointResult}>
			{children}
		</BulkOpsCoreEntryPointContext.Provider>
	);
};

export const useBulkOpsEntryPoint = () => useContext(BulkOpsCoreEntryPointContext);

export const BulkOpsWrapper = ({ children }: { children: ReactNode }) => (
	<BulkOpsAnalyticsProvider>
		<BulkOpsCoreEntryPointProvider>{children}</BulkOpsCoreEntryPointProvider>
	</BulkOpsAnalyticsProvider>
);
